import request from '@apps-common/js-client/api/request';
import {
  Box,
  Button,
  CircularProgress,
  Collapse,
  Divider,
  Snackbar as SnackbarUI,
  Tooltip,
  Typography,
  styled,
} from '@mui/material';
import {useMutation, useQuery} from '@tanstack/react-query';
import {containsExtent, getIntersection, intersects} from 'ol/extent';
import {object} from 'prop-types';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useHistory, useParams} from 'react-router-dom';
import {
  fetchSeriesInfo,
  fetchSeriesMosaics,
  getItems,
  getObservations,
} from '../api.js';
import {SORT_OPTIONS} from '../constants/index.js';
import {useResult} from '../hooks/api.js';
import {getLayerTypeAndId, isMosaicLayer} from '../util/collection.js';
import {toUTCDay} from '../util/date.js';
import {
  getExtentForGeometry,
  getMosaicsByDate,
  getVisualizationSeries,
  scaleExtent,
} from '../util/map.js';
import {formatExtraParams, parseExtraParams} from '../util/params.js';
import CollectionMap from './CollectionMap.jsx';
import {HomeLink} from './HomeLink.jsx';
import ObservationChooser from './ObservationChooser/index.jsx';
import {ReviewDetections} from './ReviewDetections.jsx';
import SoMuchLoading from './SoMuchLoading.jsx';

const Snackbar = styled(SnackbarUI)({
  zIndex: 99,
  position: 'absolute',
  minWidth: 20,
  '& .MuiSnackbarContent-root': {
    minWidth: 20,
    padding: 1,
  },
  '& .MuiSnackbarContent-message': {
    padding: 0,
  },
});

const Container = styled('div')({
  height: '100%',
  display: 'flex',
  position: 'relative',
  overflow: 'hidden',
});

const LeftSidebar = styled('div')({
  width: 400,
  display: 'flex',
  flexDirection: 'column',
});

const MapContainer = styled('div')({
  display: 'flex',
  flexGrow: 1,
  position: 'relative',
});

const ExportButton = styled(Button)({
  flex: 1,
});

const getMosaicForLayer = (mosaicsInfo, observations, layer) => {
  const observation = observations.filter(obs => obs.layer === layer)[0];

  if (!observation || !mosaicsInfo) {
    return '';
  }

  const observedDate = new Date(observation.observed);
  const candidates = getMosaicsByDate(mosaicsInfo, observedDate);
  return candidates.length > 0 ? `Mosaic:${candidates[0].name}` : '';
};

export const CollectionReport = ({subscription, feed}) => {
  const history = useHistory();
  const params = useParams();
  const extraParams = useMemo(() => parseExtraParams(params), [params]);

  // TODO: Use mosaics series to determine interval if mosaic based feed
  // Multi-input source config support: uses FIRST config for source
  const sourceType = feed.source[0].type;
  const interval = sourceType === 'image' ? 'month' : 'year';

  const changeDetection = sourceType === 'mosaic';
  const seriesId = getVisualizationSeries(feed);
  const {data: seriesInfo} = useQuery({
    queryKey: ['series', seriesId],
    queryFn: () => fetchSeriesInfo(seriesId),
    enabled: changeDetection,
  });

  const {data: mosaicsInfo} = useQuery({
    queryKey: ['mosaics', seriesId],
    queryFn: () => fetchSeriesMosaics(seriesId),
    enabled: changeDetection,
  });

  // Setup Default End Date
  let defaultEndDate = extraParams.endDate
    ? new Date(extraParams.endDate)
    : toUTCDay(new Date(), 1);
  if (subscription.endTime && defaultEndDate > new Date(subscription.endTime)) {
    defaultEndDate = new Date(subscription.endTime);
  }

  // Setup Default Start Date
  const defaultStartDate = extraParams.startDate
    ? new Date(extraParams.startDate)
    : new Date(subscription.startTime);

  const [startDate, updateStartDate] = useState(defaultStartDate);
  const [endDate, updateEndDate] = useState(defaultEndDate);

  const mapRef = useRef();
  const [showRedo, setShowRedo] = useState(false);
  const [showZero, setShowZero] = useState(false);
  const toggleZero = useCallback(() => setShowZero(!showZero), [showZero]);
  const [showReview, setShowReview] = useState(false);
  const [detections, setDetections] = useState([]);
  const [sortOrder, setSortOrder] = useState(SORT_OPTIONS[0]);

  const defaultFilterValues = {
    confidence: [0, 1],
    observed: [startDate, endDate],
    classes: feed.outputClasses ? feed.outputClasses.map(c => c.name) : [],
  };
  const [filterValues, setFilterValues] = useState(defaultFilterValues);

  const [result, updateResult] = useState('');

  const [observations, setObservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mapReady, setMapReady] = useState(false);
  const [selectedObservation, setSelectedObservation] = useState(null);

  const initialExtent = getExtentForGeometry(subscription.geometry);
  scaleExtent(initialExtent, 1.25);
  const [view, updateView] = useState(
    extraParams.center && extraParams.zoom
      ? {
          center: extraParams.center,
          zoom: extraParams.zoom,
        }
      : {
          extent: initialExtent,
        },
  );

  const {data: resultInfo, loading: resultInfoLoading} = useResult(
    subscription.id,
    result,
  );

  let displayLayer = extraParams.layer || '';
  if (!resultInfoLoading && resultInfo) {
    const {source_item_type, source_item_id, source_mosaic_name} =
      resultInfo.properties;
    if (source_mosaic_name) {
      displayLayer = `Mosaic:${source_mosaic_name}`;
    } else if (source_item_type && source_item_id) {
      displayLayer = `${source_item_type}:${source_item_id}`;
    }
  } else if (selectedObservation) {
    displayLayer = selectedObservation.layer;
  } else if (!loading && !displayLayer && observations.length > 0) {
    for (const observation of observations) {
      if (showZero || observation.count > 0) {
        displayLayer = observation.layer;
        break;
      }
    }
  }

  useEffect(() => {
    if (view && view.center && displayLayer) {
      const extras = formatExtraParams({
        loc: view,
        displayLayer,
        dates: {
          startDate,
          endDate,
        },
      });
      history.replace(`/subscription/${subscription.id}/${extras}`);
    }
  }, [displayLayer, endDate, history, startDate, subscription.id, view]);

  const clearData = useCallback(() => {
    setLoading(true);
    setObservations([]);
    updateResult('');
  }, []);

  const updateDateRange = useCallback(
    range => {
      clearData();

      // Update date ranges
      if (range[0].getTime() !== startDate.getTime()) {
        updateStartDate(range[0]);
      }
      if (range[1].getTime() !== endDate.getTime()) {
        updateEndDate(range[1]);
      }
    },
    [clearData, endDate, startDate],
  );

  const getBbox = useCallback(() => {
    if (!mapRef.current) {
      return null;
    }
    const extent = mapRef.current.getViewExtent();
    if (extent) {
      setShowRedo(false);
    }
    return extent;
  }, []);

  const loadObservations = useCallback(() => {
    if (!mapReady || (changeDetection && !mosaicsInfo)) {
      return;
    }

    let bbox = getBbox();
    if (
      bbox &&
      containsExtent(bbox, getExtentForGeometry(subscription.geometry))
    ) {
      // optimization to avoid intersection test in the query
      bbox = null;
    }
    let queryStartDate = null;
    let queryEndDate = null;
    if (
      startDate > new Date(subscription.startTime) ||
      endDate < new Date(subscription.endTime)
    ) {
      queryStartDate = startDate;
      queryEndDate = endDate;
    }
    getObservations(subscription.id, queryStartDate, queryEndDate, bbox).then(
      ({data}) => {
        setObservations(data);
        setLoading(false);
      },
    );
  }, [
    mapReady,
    changeDetection,
    mosaicsInfo,
    getBbox,
    subscription,
    startDate,
    endDate,
  ]);

  // Initial Observations Load
  useEffect(() => {
    loadObservations();
  }, [loadObservations]);

  const onObservationClick = useCallback(
    (observation, zoomIn) => {
      updateResult('');
      setSelectedObservation(observation);
      if (isMosaicLayer(observation.layer)) {
        return;
      }

      // Zoom to the extent of the intersection of the selected observation and subscription geometry
      // only if the map extent doesn't already intersect it.
      const [layerType, layerId] = getLayerTypeAndId(observation.layer);
      const url = `https://api.planet.com/data/v1/item-types/${layerType}/items/${layerId}`;
      request
        .get(url)
        .then(response => {
          const geometry = response.data.geometry;
          const extent = getIntersection(
            getExtentForGeometry(geometry),
            getExtentForGeometry(subscription.geometry),
          );

          const currentExtent = mapRef.current.getViewExtent();
          if (zoomIn || !intersects(currentExtent, extent)) {
            mapRef.current.zoomToExtent(extent);
          }
        })
        .catch(() => {
          // Do nothing
        });
    },
    [subscription.geometry],
  );

  const onObservationZoomClick = useCallback(
    observation => onObservationClick(observation, true),
    [onObservationClick],
  );

  const handleFilterChange = useCallback(
    (id, values) => {
      setFilterValues({
        ...filterValues,
        [id]: values,
      });
    },
    [filterValues],
  );

  const handleRedoSearch = useCallback(() => {
    clearData();
    loadObservations();
  }, [clearData, loadObservations]);

  const handleMapMoveEnd = event => {
    // Using frameState index 1 to not set the show redo for first load
    if (!showRedo && event.frameState.index !== 1) {
      setShowRedo(true);
    }
    setMapReady(true);
  };

  const reviewMutation = useMutation({
    mutationFn: layer => {
      const [itemType, itemId] = layer.split(':');
      const bbox = getBbox();

      // in case there is an issue with not finding the
      //  layer in the list, default to the entire date range.
      let [qStart, qEnd] = [startDate, endDate];
      const obs = observations.filter(obs => obs.layer === layer)[0];
      if (obs) {
        qStart = new Date(obs.observed);
        qEnd = new Date(qStart.getTime() + 60000);
      }

      return getItems(subscription.id, qStart, qEnd, bbox).then(features => {
        const selectedFeatures = features
          .filter(f => {
            const item = f.properties;
            // some old feeds might have `source_item` and new feeds might have `source_item_id`
            const candidateId = item.source_item_id || item.source_item;
            return (
              (item.source_item_type === itemType && candidateId === itemId) ||
              (itemType === 'Mosaic' && item.source_mosaic_name === itemId)
            );
          })
          .sort((a, b) => {
            // descending by score.
            return a.properties.score < b.properties.score ? 1 : -1;
          });
        setDetections(selectedFeatures);
        setShowReview(true);
      });
    },
  });

  return (
    <Container>
      {detections && detections.length > 0 && showReview && (
        <ReviewDetections
          changeDetection={changeDetection}
          open
          onClose={() => setShowReview(false)}
          detections={detections}
          layer={displayLayer}
          visualLayer={getMosaicForLayer(
            mosaicsInfo,
            observations,
            displayLayer,
          )}
          feed={feed}
        />
      )}
      <LeftSidebar>
        <Box py={1} px={2}>
          <HomeLink />
          <Box py={1}>
            <Tooltip title={subscription.title}>
              <Typography noWrap variant="h6" pb={1}>
                {subscription.title}
              </Typography>
            </Tooltip>
            <Typography noWrap variant="body1">
              {subscription.description}
            </Typography>
          </Box>
        </Box>
        <ObservationChooser
          interval={interval}
          loading={loading}
          minDate={new Date(subscription.startTime)}
          maxDate={subscription.endTime && new Date(subscription.endTime)}
          dateRange={[startDate, endDate]}
          observations={observations}
          selected={displayLayer}
          onObservationClick={onObservationClick}
          onObservationZoomClick={onObservationZoomClick}
          onDateRangeChange={updateDateRange}
          toggleZero={toggleZero}
          showZero={showZero}
          seriesInfo={seriesInfo}
          sortOrder={sortOrder}
          onSortOrderChange={nextSortOrder => {
            setLoading(true);
            setSortOrder(nextSortOrder);
            setLoading(false);
          }}
        />
        <Collapse in={!!displayLayer}>
          <Box>
            <Divider />
            <Box display="flex" py={1} px={2} textAlign="center">
              <ExportButton
                variant="contained"
                color="primary"
                size="large"
                disabled={reviewMutation.isPending}
                startIcon={
                  reviewMutation.isPending && <CircularProgress size={20} />
                }
                onClick={() => {
                  reviewMutation.mutate(displayLayer);
                }}
              >
                Review &amp; export detects
              </ExportButton>
            </Box>
          </Box>
        </Collapse>
      </LeftSidebar>
      <MapContainer>
        <SoMuchLoading loading={loading || resultInfoLoading} />
        <CollectionMap
          ref={mapRef}
          subscription={subscription}
          feed={feed}
          layer={displayLayer}
          visualLayer={getMosaicForLayer(
            mosaicsInfo,
            observations,
            displayLayer,
          )}
          observations={observations}
          resultInfo={resultInfo}
          onSelectResult={updateResult}
          onMoveEnd={handleMapMoveEnd}
          changeDetection={changeDetection}
          handleFilterChange={handleFilterChange}
          filterValues={filterValues}
          defaultFilterValues={defaultFilterValues}
          view={view}
          updateView={nextView => updateView(nextView)}
          mosaicsInfo={mosaicsInfo}
        />
        <Snackbar
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
          open={showRedo}
          message={
            <Button onClick={handleRedoSearch} color="primary">
              Redo search in this area
            </Button>
          }
        ></Snackbar>
      </MapContainer>
    </Container>
  );
};

CollectionReport.propTypes = {
  subscription: object.isRequired,
  feed: object.isRequired,
};

export default CollectionReport;
